import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/components/auth/AuthProvider';
import { FullPageLoader } from '@/components/common/LoadingBoundary';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from '@/components/optimization/ErrorFallback';
import { EnhancedLayout } from '@/components/navigation/EnhancedLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Lazy load major components
const LazyDashboard = React.lazy(() => import('@/components/dashboard/Dashboard').then(m => ({ default: m.Dashboard })));
const LazyRunning = React.lazy(() => import('@/components/running/Running').then(m => ({ default: m.Running })));
const LazyBodyComposition = React.lazy(() => import('@/components/body-composition/BodyComposition').then(m => ({ default: m.BodyComposition })));
const LazyPhotos = React.lazy(() => import('@/components/photos/PhotosPage').then(m => ({ default: m.PhotosPage })));
const LazyActivityTracker = React.lazy(() => import('@/pages/ActivityTracker'));
const LazyAICoachFitness = React.lazy(() => import('@/pages/AICoachFitness'));
const LazyAICoach = React.lazy(() => import('@/pages/AICoach').then(m => ({ default: m.AICoach })));
const LazyAnalyticsDashboard = React.lazy(() => import('@/components/dashboard/AnalyticsDashboard').then(m => ({ default: m.AnalyticsDashboard })));
const LazyProfileSettings = React.lazy(() => import('@/components/profile/ProfileSettings').then(m => ({ default: m.ProfileSettings })));
const LazyAchievements = React.lazy(() => import('@/pages/Achievements').then(m => ({ default: m.Achievements })));
const LazyNutritionPage = React.lazy(() => import('@/pages/nutrition').then(m => ({ default: m.NutritionPage })));
const LazyAuthPage = React.lazy(() => import('@/components/auth/AuthPageLight').then(m => ({ default: m.AuthPageLight })));

const RouteWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ErrorBoundary FallbackComponent={ErrorFallback}>
    <Suspense fallback={<FullPageLoader message="Loading..." />}>
      {children}
    </Suspense>
  </ErrorBoundary>
);

export const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Routes>
            <Route path="/auth" element={
              <RouteWrapper>
                <LazyAuthPage />
              </RouteWrapper>
            } />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route element={<ProtectedRoute><EnhancedLayout><Outlet /></EnhancedLayout></ProtectedRoute>}>
              <Route path="/dashboard" element={
                <RouteWrapper>
                  <LazyDashboard />
                </RouteWrapper>
              } />
              <Route path="/running" element={
                <RouteWrapper>
                  <LazyRunning />
                </RouteWrapper>
              } />
              <Route path="/body-composition" element={
                <RouteWrapper>
                  <LazyBodyComposition />
                </RouteWrapper>
              } />
              <Route path="/photos" element={
                <RouteWrapper>
                  <LazyPhotos />
                </RouteWrapper>
              } />
              <Route path="/activity-tracker" element={
                <RouteWrapper>
                  <LazyActivityTracker />
                </RouteWrapper>
              } />
              <Route path="/ai-coach-fitness" element={
                <RouteWrapper>
                  <LazyAICoachFitness />
                </RouteWrapper>
              } />
              <Route path="/ai-coach" element={
                <RouteWrapper>
                  <LazyAICoach />
                </RouteWrapper>
              } />
              <Route path="/analytics" element={
                <RouteWrapper>
                  <LazyAnalyticsDashboard />
                </RouteWrapper>
              } />
              <Route path="/profile" element={
                <RouteWrapper>
                  <LazyProfileSettings />
                </RouteWrapper>
              } />
              <Route path="/achievements" element={
                <RouteWrapper>
                  <LazyAchievements />
                </RouteWrapper>
              } />
              <Route path="/nutrition" element={
                <RouteWrapper>
                  <LazyNutritionPage />
                </RouteWrapper>
              } />
            </Route>
          </Routes>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
};